import { type FC, type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';

import { useAuth, useNotifications, useRequest } from '../../../modules/hooks';
import type {
  PropertyRegistrationStep,
  UserProperties,
  Users,
} from '../../../modules/interfaces';
import { endpoints } from '../../../modules/mappers/urls';

export const UserProvider: FC<PropsWithChildren> = ({ children }) => {
  const [userPropertiesData, setUserPropertiesData] = useState<Users>();
  const [isValidSelectedProperty, setIsValidSelectedProperty] = useState<boolean>(true);
  const [selectedProperty, setSelectedProperty] = useState<UserProperties | undefined>();
  const [propertyRegistrationStep, setPropertyRegistrationStep] = useState<
    PropertyRegistrationStep[] | undefined
  >();
  const { credentialsInfo } = useAuth()!;
  const { setSimpleToasts } = useNotifications()!;

  const userPropertiesUrl = `${endpoints.USERS}/info`;
  const [{ data, loading: userPropertiesLoading, error: userPropertiesError }, getInfo] =
    useRequest<Users>(
      userPropertiesUrl,
      'get',
      {
        authToken: credentialsInfo?.token,
      },
      { manual: true },
    );

  const url = `${endpoints.PROPERTIES}/${selectedProperty?.property?.uuid}/registration-steps`;
  const [{ data: propertyRegistrationStepResult }, getPropertySteps] = useRequest<
    PropertyRegistrationStep[]
  >(
    url,
    'get',
    {
      authToken: credentialsInfo?.token,
    },
    { manual: true },
  );

  useEffect(() => {
    if (selectedProperty) {
      getPropertySteps();
    }
  }, [selectedProperty]);

  useEffect(() => {
    if (propertyRegistrationStepResult && propertyRegistrationStepResult.result) {
      setPropertyRegistrationStep(propertyRegistrationStepResult.result);
    }
  }, [propertyRegistrationStepResult]);

  useEffect(() => {
    if (!credentialsInfo?.token) return;

    getInfo();
  }, [credentialsInfo?.token]);

  useEffect(() => {
    if (data) {
      setUserPropertiesData(data.result);
    }
    if (userPropertiesError) {
      setSimpleToasts({
        type: 'danger',
        message: 'Error downloading agreement PDF',
        show: true,
      });
    }
  }, [data, userPropertiesError]);

  const propertiesContext = useMemo(() => {
    return {
      userPropertiesData,
      userPropertiesLoading,
      selectedProperty,
      isValidSelectedProperty,
      setSelectedProperty,
      setIsValidSelectedProperty,
      getInfo,
      propertyRegistrationStep,
    };
  }, [
    userPropertiesData,
    userPropertiesLoading,
    selectedProperty,
    isValidSelectedProperty,
    setSelectedProperty,
    setIsValidSelectedProperty,
    getInfo,
    propertyRegistrationStep,
  ]);

  return (
    <UserPropertiesContext.Provider value={propertiesContext}>
      {children}
    </UserPropertiesContext.Provider>
  );
};
